import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Images,
  Eye,
  Download,
  Plus,
  Settings,
  Search,
  MoreVertical,
  ExternalLink,
  Trash2,
  Copy,
  Loader2,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleriesPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries`);
      setGalleries(response.data);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (galleryId) => {
    if (!window.confirm(t('gallery.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/galleries/${galleryId}`);
      setGalleries(galleries.filter(g => g.id !== galleryId));
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const copyLink = (publicId) => {
    const url = `${window.location.origin}/g/${publicId}`;
    navigator.clipboard.writeText(url);
    toast.success(t('gallery.linkCopied'));
  };

  const filteredGalleries = galleries.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    (g.client_name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/galleries" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold">{t('dashboard.stats.galleries')}</h1>
          <Button onClick={() => navigate('/galleries/new')} data-testid="create-gallery-header-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('gallery.create')}
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 max-w-md"
            data-testid="gallery-search-input"
          />
        </div>

        {/* Galleries Grid */}
        {filteredGalleries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredGalleries.map((gallery, idx) => (
              <motion.div
                key={gallery.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
              >
                <Card className="bg-card border-border hover:border-primary/30 transition-all group">
                  <CardContent className="p-0">
                    {/* Gallery Preview / Cover Photo */}
                    <div 
                      className="aspect-video bg-secondary/50 relative cursor-pointer overflow-hidden"
                      onClick={() => navigate(`/galleries/${gallery.id}`)}
                    >
                      {gallery.cover_url ? (
                        <img 
                          src={gallery.cover_url}
                          alt={gallery.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                          <Images className="w-12 h-12" />
                        </div>
                      )}
                      {gallery.has_password && (
                        <div className="absolute top-3 left-3 bg-black/70 rounded-sm px-2 py-1 flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          <span className="text-xs">Protected</span>
                        </div>
                      )}
                      <div className="absolute bottom-3 right-3 bg-black/70 rounded-sm px-2 py-1">
                        <span className="text-xs">{gallery.photo_count} {t('gallery.photoCount')}</span>
                      </div>
                    </div>

                    {/* Gallery Info */}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 
                            className="font-semibold truncate cursor-pointer hover:text-primary"
                            onClick={() => navigate(`/galleries/${gallery.id}`)}
                          >
                            {gallery.name}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">{gallery.client_name}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/galleries/${gallery.id}`)}>
                              <Settings className="w-4 h-4 mr-2" />
                              {t('gallery.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open(`/g/${gallery.public_id}`, '_blank')}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              {t('gallery.viewGallery')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => copyLink(gallery.public_id)}>
                              <Copy className="w-4 h-4 mr-2" />
                              {t('gallery.copyLink')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(gallery.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('gallery.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {gallery.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          {gallery.downloads}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Images className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                {search ? t('common.noResults') : t('dashboard.noGalleries')}
              </h3>
              {!search && (
                <Button onClick={() => navigate('/galleries/new')} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('gallery.create')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
